import { action, computed, makeObservable, observable, override, reaction } from 'mobx';

import { CheckoutStep } from '../../app/checkout/ICheckoutStep';
import type { ICheckoutStep } from '../../app/checkout/ICheckoutStep';
import { ConfiguratorUrlBuilder } from '../../app/configurator/ConfiguratorUrlBuilder';
import { IApiClient } from '../../app/data/client';
import {
  CreateDealerOrderCommand,
  GetDealerCartQuery,
  MoneyData,
  RemoveModularProductFromDealerCartCommand,
  RemoveProductFromDealerCartCommand,
  ShoppingCartData,
  ShoppingCartItemData,
  SurchargeData,
  UpdateModularProductQuantityInDealerCartCommand,
  UpdateProductQuantityInDealerCartCommand,
} from '../../app/data/model';
import type {
  CreateDealerOrderCommandResponse,
  DealerCartsData,
  ShoppingCartItemGroupData,
  ShoppingCartSummaryData,
} from '../../app/data/model';
import { BasePageState } from '../../app/shared/BasePageState';
import { AsyncCommand } from '../../app/shared/common';
import type { PageMetadata } from '../../app/shared/common';
import { IEventAggregator } from '../../app/shared/EventAggregator';
import { CheckboxInput, Form, TextInput, ValueMaxLength } from '../../app/shared/Form';
import { LineItemActionToggles } from '../../app/shared/LineItemActionToggles';
import { ModalState } from '../../app/shared/ModalState';
import { INavigationService } from '../../app/shared/NavigationService';
import { ZipBuilder } from '../../app/shared/ZipBuilder';
import { StoreState } from '../../app/StoreState';
import { CartPageTranslation, OrderCreatedModalTranslation } from '../localization/SiteTranslation';
import { BuyerPolicy } from '../Policy';
import { ICartItemContextAction, IContextAction } from './CartItem/CartItemState';
import { ICartItemGroupContextAction } from './CartItemGroup/CartItemGroupState';
import { CartState } from './CartState';
import { DeliveryAddressFormState } from './DeliveryAddressFormState';
import { ShoppingCartUpdatedEvent } from './events/ShoppingCartUpdatedEvent';
import OrderCreationConfirmationModalState from './OrderCreationConfirmationModal/OrderCreationConfirmationModalState';
import { SendCartToEmailModalState } from './SendCartToEmail/SendCartToEmailModalState';

export class OrderCreationResultModal extends ModalState {
  @observable.ref
  result: CreateDealerOrderCommandResponse;

  @computed
  get failedCarts() {
    return this.result.dealerCarts.carts.filter((x) => this.result.failedShoppingCartIds.contains(x.id));
  }

  @action
  setResponse(response: CreateDealerOrderCommandResponse) {
    this.result = response;
  }

  constructor(public translation: OrderCreatedModalTranslation) {
    super();
    makeObservable(this);
  }
}

export class CartListState extends BasePageState<null> {
  @override
  get metadata(): PageMetadata {
    return {
      title: this.translation.cartList.cart,
    };
  }

  cartItemContextActions: ICartItemContextAction[] = [];
  cartItemGroupContextActions: ICartItemGroupContextAction[] = [];

  @observable.ref
  private cartsData: ShoppingCartData[] = [];

  @observable
  public carts: CartState[] = [];

  @observable.ref
  public allCartsSummary?: ShoppingCartSummaryData;

  public steps: Array<ICheckoutStep> = [];
  public cartOverviewStep: CheckoutStep;
  public cartSummaryStep: CheckoutStep;

  public selectAllCheckbox: CheckboxInput = new CheckboxInput(false);
  public orderCreationConfirmationModal = new OrderCreationConfirmationModalState(
    this.store.storeResponse.storeContext.store,
    this.translation.orderCreationConfirmationModal,
  );
  public orderCreationResultModal = new OrderCreationResultModal(this.translation.orderCreatedModal);
  public sendOrderToEmailModal: SendCartToEmailModalState;

  public deliveryAddress: DeliveryAddressFormState;
  public placeOrderForm: Form;
  public commentInput: TextInput;
  public deliveryInstructions: TextInput;

  private zipBuilder: ZipBuilder;

  public readonly goToCartSummaryCommand: AsyncCommand;
  public readonly placeOrderCommand: AsyncCommand;

  public get hasUserAccessToMultipleStores() {
    return this.store.areOtherStoresAvailable;
  }

  constructor(
    private client: IApiClient,
    private eventAggregator: IEventAggregator,
    private store: StoreState,
    private navigation: INavigationService,
    public translation: CartPageTranslation,
  ) {
    super();
    makeObservable(this);
    this.zipBuilder = new ZipBuilder(this.client, this.navigation);

    this.cartOverviewStep = new CheckoutStep('cart-overview', this.translation.cartList.cartOverviewTitle);
    this.cartSummaryStep = new CheckoutStep('cart-summary', this.translation.cartList.cartSummaryTitle);

    this.steps = [this.cartOverviewStep, this.cartSummaryStep];
    this.setStep(this.cartOverviewStep);

    const buyerContextActions = [
      {
        label: this.translation.cartList.editProductLabel,
        action: this.redirectToItemConfiguration,
        condition: LineItemActionToggles.canEdit,
      },
      {
        label: this.translation.cartList.removeFromCardLabel,
        action: this.removeProductFromCart,
        separate: true,
      },
    ];

    const memberContextActions = [
      {
        label: this.translation.cartList.downloadImagesLabel,
        action: this.generateZipVisualizationImages,
        condition: LineItemActionToggles.hasImages,
      },
    ];

    this.cartItemContextActions = BuyerPolicy.grantAccess(this.store.shoppingContext.customer)
      ? [...buyerContextActions, ...memberContextActions]
      : memberContextActions;

    this.cartItemGroupContextActions = BuyerPolicy.grantAccess(this.store.shoppingContext.customer)
      ? [
          {
            label: this.translation.cartList.removeFromCardLabel,
            action: this.removeModularProductFromCart,
          },
        ]
      : [];

    this.goToCartSummaryCommand = new AsyncCommand(() => {
      this.setStep(this.cartSummaryStep);
      return Promise.resolve();
    });

    this.commentInput = new TextInput().withRule(new ValueMaxLength(60));
    this.deliveryInstructions = new TextInput().withRule(new ValueMaxLength(50));
    this.deliveryAddress = new DeliveryAddressFormState(
      store.shoppingContext,
      client,
      translation.deliveryAddress.deliveryAddressForm,
    );
    this.placeOrderForm = new Form();
    this.placeOrderForm.inputsToValidate.push(this.commentInput);
    this.placeOrderForm.inputsToValidate.push(this.deliveryInstructions);
    this.placeOrderCommand = new AsyncCommand(this.placeOrder, this.placeOrderForm);
    this.sendOrderToEmailModal = new SendCartToEmailModalState(
      client,
      store.shoppingContext,
      this.translation.sendCartToEmail,
    );

    reaction(
      () => this.cartsData,
      (cartsData) => {
        this.carts = cartsData.map(
          (cartData) =>
            new CartState(
              cartData,
              this.cartItemContextActions,
              this.cartItemGroupContextActions,
              this.changeItemQuantity,
              this.changeItemGroupQuantity,
              this.translation.cartList.cartItemList,
            ),
        );
      },
    );

    this.setupCheckboxEvents();
  }

  async onLoad(store: StoreState): Promise<void> {
    const cartResponse = await this.client.send(new GetDealerCartQuery({ shoppingContext: store.shoppingContext }));

    this.setDealerCarts(cartResponse.dealerCarts);
    this.selectAllCarts();
  }

  @computed
  public get selectedCarts() {
    return this.carts.filter((bc) => bc.selected.value === true);
  }

  @computed
  public get selectedCartsCount() {
    return this.selectedCarts.length;
  }

  @computed
  public get selectedCartsItemsCount() {
    return this.selectedCarts.empty() ? 0 : this.selectedCarts.sum((x) => x.numberOfItems);
  }

  @computed get footerButtonAction(): { label: string; command: AsyncCommand } | undefined {
    let noSelectedOrders = this.selectedCartsCount === 0;

    if (this.cartOverviewStep.isActive) {
      return {
        label: noSelectedOrders
          ? this.translation.cartList.noneOrdersSelectedLabel
          : this.translation.cartList.continueWithOrdersLabelFormat.interpolate([
              ['ordersCount', this.selectedCartsCount.toString()],
            ]),
        command: this.goToCartSummaryCommand,
      };
    }
    if (this.cartSummaryStep.isActive) {
      return {
        label: this.translation.cartList.placeAllOrdersLabelFormat.interpolate([
          ['ordersCount', this.selectedCartsCount.toString()],
        ]),
        command: this.placeOrderCommand,
      };
    }
  }

  private sumPrices(prices: MoneyData[]): MoneyData {
    if (prices.empty()) {
      throw new Error('Unable to sum empty price list');
    }
    return { amount: prices.sum((x) => x.amount), currency: prices[0].currency };
  }

  @computed
  public get summaryShoppingCart() {
    if (this.selectedCarts.empty()) {
      const emptyPrice: MoneyData = { amount: 0, currency: { code: '' } };
      const emptySurcharges: SurchargeData[] = [];
      return {
        subTotal: emptyPrice,
        subTotalTax: emptyPrice,
        subTotalWithTax: emptyPrice,
        totalWithTax: emptyPrice,
        taxRatePercentage: 0,
        surcharges: emptySurcharges,
      };
    }

    return {
      subTotal: this.sumPrices(this.selectedCarts.map((x) => x.shoppingCart.subTotal)),
      subTotalTax: this.sumPrices(this.selectedCarts.map((x) => x.shoppingCart.subTotalTax)),
      subTotalWithTax: this.sumPrices(this.selectedCarts.map((x) => x.shoppingCart.subTotalWithTax)),
      totalWithTax: this.sumPrices(this.selectedCarts.map((x) => x.shoppingCart.totalWithTax)),
      taxRatePercentage: this.carts[0].shoppingCart.taxRatePercentage,
      surcharges: this.carts.mapMany((x) => x.shoppingCart.surcharges),
    };
  }

  @action.bound
  public setDealerCarts(dealerCarts: DealerCartsData) {
    this.cartsData = dealerCarts.carts;
    this.allCartsSummary = dealerCarts.cartsSummary;
    this.eventAggregator.publish(new ShoppingCartUpdatedEvent(dealerCarts.cartsSummary));
  }

  @action.bound setStep(step: ICheckoutStep) {
    this.steps.forEach((x) => (x.isActive = x === step));

    if (step === this.cartSummaryStep && this.selectedCarts.any()) {
      const cartState = this.selectedCarts.first(); // Selection disabled, so take the first one

      this.placeOrderForm.clearChildForms();
      this.placeOrderForm.childForms.push(cartState);
      this.placeOrderForm.childForms.push(this.deliveryAddress);
      this.placeOrderForm.resetValidation();

      this.deliveryAddress.initialize(cartState.shoppingCart);
    }
  }

  public placeOrder = async () => {
    const command = new CreateDealerOrderCommand({
      shoppingCarts: this.selectedCarts.map((x) => {
        return { id: x.cartId, customName: x.customName.value, customReference: x.customReference.value };
      }),
      comment: this.commentInput.value,
      deliveryAddress: this.deliveryAddress.getAddress(),
      deliveryInstructions: this.deliveryInstructions.value,
      shoppingContext: this.store.shoppingContext,
    });
    const response = await this.client.send(command);

    this.orderCreationConfirmationModal.close();

    this.orderCreationResultModal.setResponse(response);
    await this.orderCreationResultModal.open();

    this.setDealerCarts(response.dealerCarts);

    if (response.success || response.invalidConfigurationCodes.any()) {
      this.setStep(this.cartOverviewStep);
    }
  };

  public openOrderConfirmation = async () => {
    this.placeOrderForm.validate();

    if (this.placeOrderForm.isValid) {
      await this.orderCreationConfirmationModal.open();
    }
  };

  private setupCheckboxEvents() {
    reaction(
      () => this.carts.all((bc) => bc.selected.value === true),
      (allSelected) => {
        this.selectAllCheckbox.value = allSelected;
      },
    );

    this.selectAllCheckbox.valueChanged.subscribe((value) => {
      this.carts.forEach((bc) => bc.selected.onChange(value));
    });
  }

  private selectAllCarts() {
    this.selectAllCheckbox.onChange(true);
  }

  private changeItemQuantity = async (item: ShoppingCartItemData, newQuantity: number) => {
    const response = await this.client.send(
      new UpdateProductQuantityInDealerCartCommand({
        quantity: newQuantity,
        lineItemId: item.id,
        shoppingContext: this.store.shoppingContext,
      }),
    );

    this.setDealerCarts(response.dealerCarts);
  };

  private changeItemGroupQuantity = async (group: ShoppingCartItemGroupData, newQuantity: number) => {
    const response = await this.client.send(
      new UpdateModularProductQuantityInDealerCartCommand({
        quantity: newQuantity,
        lineItemGroupId: group.id,
        shoppingContext: this.store.shoppingContext,
      }),
    );

    // this.trackItemQuantityChange(item, newQuantity);

    this.setDealerCarts(response.dealerCarts);
  };

  private redirectToItemConfiguration = async (item: ShoppingCartItemData) => {
    const url = ConfiguratorUrlBuilder.buildFromCartItem(item);
    this.navigation.navigateTo(url);
  };

  private removeProductFromCart = async (item: ShoppingCartItemData) => {
    const response = await this.client.send(
      new RemoveProductFromDealerCartCommand({
        lineItemId: item.id,
        shoppingContext: this.store.shoppingContext,
      }),
    );

    this.setDealerCarts(response.dealerCarts);
  };

  private removeModularProductFromCart = async (group: ShoppingCartItemGroupData) => {
    const response = await this.client.send(
      new RemoveModularProductFromDealerCartCommand({
        lineItemGroupId: group.id,
        shoppingContext: this.store.shoppingContext,
      }),
    );

    // this.trackRemoveFromCartAction(item);

    this.setDealerCarts(response.dealerCarts);
  };

  private generateZipVisualizationImages = async (item: ShoppingCartItemData) => {
    const zipUrl = await this.zipBuilder.downloadZipConfigurationImages(item.code);
  };

  @action.bound
  public goToCartSummary(cart: CartState) {
    this.selectSpecificCart(cart);
    this.setStep(this.cartSummaryStep);
  }

  @action.bound
  public openSendConfigurationToEmailModal = (cart: CartState) => {
    this.sendOrderToEmailModal.setSelectedShoppingCartsIds([cart.shoppingCart.id]);
    this.sendOrderToEmailModal.open();
  };

  private selectSpecificCart(cart: CartState) {
    this.carts.forEach((x) => x.selected.setDefaultValue(false));
    cart.selected.setDefaultValue(true);
  }
}
